// === GENERATED:SCREEN:WORKFLOW-SUMMARY ===
// Generated by Sluitstraat Generator
// Redesigned summary page with offer overview

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  useConfig,
  WorkflowPageLayout,
  getPreviousScreen,
  isDebugMode,
} from '@sluitstraat/core';
import { workflowSummaryStyles as styles } from '../../styles/commonStyles';
import { HelpCard } from '../../components/HelpCard';
import { SubscriptionCard } from '../../components/SubscriptionCard';
import { getProductOrder } from '../../workflowConfig';
import { hasAnySelectedProduct } from '../../utils/productGuards';
import { ApplicantSummary } from '../../components/ApplicantSummary';
import {
  SUMMARY_SESSION_KEYS,
  DocumentsCard,
  PremiumTotalsCard,
  computeProductDetails,
  displayDiscountFactor,
  extractPackageDiscount,
  getQuoteId,
  readEnrichedDocument,
  readFlag,
  useOfferChain,
} from './summary';


export const WorkflowSummaryScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, selectedProducts } = useWorkflow();
  const { config, isLoading: isConfigLoading } = useConfig();
  // Servicevergoeding (PP_EKST) - config-driven via prolongationFee (vaste 2.50 per product)
  const ppEkstConfig = (config as any)?.prolongationFee;
  const ppEkst = ppEkstConfig?.enabled === false ? 0 : (ppEkstConfig?.amount ?? 2.5);
  // Offer chain (relation → calculate → package discount → quote): states, hash and
  // orchestration effect moved verbatim to summary/useOfferChain.
  const { relationCreated, premiumCalculated, packageDiscountApplied, quoteGenerated, relationError, isLoadingRelation } =
    useOfferChain({ data, selectedProducts, config, t, ppEkst, baseUrl: window.location.origin + '/' });
  const [isBackButtonHovered, setIsBackButtonHovered] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Validate required data on mount - redirect if missing critical data
  useEffect(() => {
    // Check if at least one product is selected (generic — a hardcoded per-product list
    // here silently blocks any product not enumerated, e.g. doorlopendereis).
    const hasAnyProduct = hasAnySelectedProduct(selectedProducts);
    if (!hasAnyProduct) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }

    // Check if client information exists
    const clientInfo = data['client-information']?.clientInfo;
    if (!clientInfo || !clientInfo.email || !clientInfo.initials || !clientInfo.lastName) {
      navigate('/workflow/client-information', { replace: true });
      return;
    }

    // Check if at least one product has been configured (has calculation data with premium)
    // Note: AVP stores premium in DA.DA_TTOT, not PP_TTOT
    const hasAnyCalculationData =
      (selectedProducts?.aansprakelijkheid && (data['aansprakelijkheid']?.Contractdocument?.PP?.DA?.DA_TTOT || data['aansprakelijkheid']?.Contractdocument?.PP?.PP_TTOT)) ||
      (selectedProducts?.woonhuis && data['woonhuis']?.Contractdocument?.PP?.PP_TTOT) ||
      (selectedProducts?.inboedel && data['inboedel']?.Contractdocument?.PP?.PP_TTOT) ||
      (selectedProducts?.personenauto && data['personenauto']?.Contractdocument?.PP) ||
      (selectedProducts?.rechtsbijstand && data['rechtsbijstand']?.Contractdocument?.PP) ||
      (selectedProducts?.ongevallen && (data['ongevallen']?.Contractdocument?.PP?.PP_TTOT || data['ongevallen']?.Contractdocument?.PP?.OD?.OD_NTP)) ||
      (selectedProducts?.doorlopendereis && data['doorlopendereis']?.Contractdocument?.PP?.PP_TTOT);

    if (!hasAnyCalculationData) {
      // Redirect to the first selected product screen, in the configured product order.
      const firstSelected = getProductOrder().find((id) => selectedProducts?.[id]);
      if (firstSelected) {
        navigate(`/workflow/${firstSelected}`, { replace: true });
      }
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on data/selectedProducts/navigate identity would redirect mid-flow
  }, []); // Run once on mount

  // UI discount factors for display (when applyToData is false, data is undiscounted)
  const whDisplayCfg = (config as any)?.woonhuisDiscount;
  const ibDisplayCfg = (config as any)?.inboedelDiscount;
  const whFactor = displayDiscountFactor(whDisplayCfg);
  const ibFactor = displayDiscountFactor(ibDisplayCfg);
  // AVP discount is baked into the data at calculation time, no UI overlay needed
  const avpFactor = 1;

  // Get package discount information and new premium (using TTOT = total with taxes/costs)
  const packageDiscount = extractPackageDiscount(readEnrichedDocument(), { whFactor, ibFactor, avpFactor });

  // Check sessionStorage directly for whether discount should be shown
  // This is more reliable than React state which can be reset on re-renders
  const shouldShowDiscount = () => {
    return readFlag(SUMMARY_SESSION_KEYS.packageDiscountApplied) ||
           packageDiscountApplied ||
           (packageDiscount && packageDiscount.amount > 0);
  };
  const showDiscount = shouldShowDiscount();

  // Get quote ID from sessionStorage
  const quoteId = getQuoteId();

  const today = new Date();
  const validUntil = new Date(today);
  validUntil.setDate(validUntil.getDate() + 30);

  const formatDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Per-product detail lines (name / coverage / premium / commission) — moved verbatim to
  // summary/productDetails.ts (golden-tested there).
  const selectedProductsDetails = computeProductDetails({ data, selectedProducts, config, t, packageDiscount });
  const selectedProductCount = selectedProductsDetails.length;

  // Needed by the classic total-premium layout (subtotal/discount/subscription/prolongation/grand total).
  // The breakdown variant derives totals internally via <PremiumBreakdownCard />, so these are unused
  // when USE_BREAKDOWN_UI is true — kept unconditionally because the calculation is cheap.
  const monthlyPremiumTotal = selectedProductsDetails.reduce((sum, p) => sum + p.premium, 0);
  const originalPremiumTotal = selectedProductsDetails.reduce((sum, p) => {
    return sum + (p.originalPremium !== undefined ? p.originalPremium : p.premium);
  }, 0);

  const subscriptionFeeConfig = (config as any)?.subscriptionFee;
  const subscriptionFee = subscriptionFeeConfig?.enabled
    ? (selectedProductCount > 2 ? (subscriptionFeeConfig.tier3 || 20.00) : (subscriptionFeeConfig.tier1 || 12.50))
    : 0;
  const prolongationFeeConfig = (config as any)?.prolongationFee;
  const prolongationFee = prolongationFeeConfig?.enabled ? (prolongationFeeConfig.amount || 2.50) : 0;
  // When true, the premium card breaks the 21% assurantiebelasting out on its own line.
  // When false (default), tax is folded into the displayed total and a small "incl. tax" note.
  const showTaxSeparateConfig = (config as any)?.showTaxSeparate;
  const showTaxSeparate = showTaxSeparateConfig?.enabled === true;

  // CTA gating — composed here so the chain-gating stays visible next to the useOfferChain
  // call. QUIRK preserved from the original inline JSX: disabled = chainIncomplete OR loading,
  // but the greyed-out style = chainIncomplete AND loading.
  const ctaChainIncomplete =
    !relationCreated || !premiumCalculated || (selectedProductCount > 1 && !showDiscount) || !quoteGenerated || !quoteId;
  const ctaDisabled = ctaChainIncomplete || isLoadingRelation;
  const ctaDisabledStyle = ctaChainIncomplete && isLoadingRelation;

  const handleConvertToRequest = () => {
    navigate('/workflow/payment-information');
  };

  const handleBack = () => {
    // Navigate back to the last product screen using configured workflow order
    const previousScreen = getPreviousScreen('/workflow/summary', selectedProducts || {});
    if (previousScreen) {
      navigate(previousScreen);
    } else {
      navigate('/workflow/client-information');
    }
  };

  return (
    <WorkflowPageLayout currentStep={4} totalSteps={4}>
      <main style={styles.page}>
        <div className="summary-container" style={styles.container}>
          {/* Chain status (in-flight / error) renders inside the premium card —
              the status belongs where the outcome lands, not in a page banner. */}

          {/* Header — centred title/subtitle + success badge. Back link now lives inside the
              merged card (top-left of its header), not here, so the page-level band stays clean. */}
          <header style={styles.contentHeader}>
            {relationCreated && premiumCalculated && (selectedProductCount <= 1 || showDiscount) && quoteGenerated && quoteId && (
              <div style={styles.successBadge}>
                <svg style={styles.successIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
            )}
            <h1 style={styles.title}>{t('screens.offerSummary.title')}</h1>
            <p style={styles.subtitle}>
              {t('screens.offerSummary.subtitle', { count: selectedProductCount })}
            </p>
          </header>

          {/* Two-column layout — each column is its own flex stack (left: gegevens +
              documenten, right: premium + help) so they stack independently, no
              cross-column row stretching. Mobile (index.css) dissolves both wrappers
              with display: contents and orders the cards premie -> gegevens ->
              documenten -> hulp. */}
          <div className="summary-grid" style={styles.grid}>
            {/* Left column — gegevens & producten + documenten as one flex stack,
                so both desktop columns flow independently (no shared-row stretching). */}
            <div className="summary-col-left" style={styles.columnStack}>
              <div className="summary-main-card" style={styles.card}>
                <h2 style={styles.cardTitle}>
                  {t('screens.offerSummary.detailsAndProducts', { defaultValue: 'Jouw gegevens & verzekeringen' })}
                </h2>

                {/* Personal info + insured-object blocks — single source shared with the
                    confirmation page (ApplicantSummary). Product-list rows removed: the
                    per-product icons render next to each price line in the breakdown card. */}
                <ApplicantSummary />
              </div>

              {/* Documents Section — moved verbatim to summary/DocumentsCard */}
              <DocumentsCard />
            </div>

            {/* Right column — premium overview + help card as one flex stack. */}
            <div className="summary-col-right" style={styles.columnStack}>
              {/* Premium card — moved verbatim to summary/PremiumTotalsCard; all amounts and
                  the CTA gating are composed above so they stay next to the useOfferChain call. */}
              <PremiumTotalsCard
                showTaxSeparate={showTaxSeparate}
                showDiscount={showDiscount}
                packageDiscount={packageDiscount}
                monthlyPremium={monthlyPremiumTotal}
                originalPremiumTotal={originalPremiumTotal}
                subscriptionFee={subscriptionFee}
                prolongationFee={prolongationFee}
                isConfigLoading={isConfigLoading}
                isChainLoading={isLoadingRelation}
                chainError={relationError}
                ctaDisabled={ctaDisabled}
                ctaDisabledStyle={ctaDisabledStyle}
                onConvert={handleConvertToRequest}
              />
              <HelpCard className="summary-help-card" />
              <SubscriptionCard className="summary-benefits-card" />
            </div>
          </div>

          {/* Bottom navigation — Terug only; the primary action ("Nu Aanvragen") lives in
              the premium card, so the right side of the row stays empty. */}
          <nav style={styles.bottomNav}>
            <button
              onClick={handleBack}
              onMouseEnter={() => setIsBackButtonHovered(true)}
              onMouseLeave={() => setIsBackButtonHovered(false)}
              style={{ ...styles.bottomNavBack, ...(isBackButtonHovered ? styles.bottomNavBackHover : {}) }}
            >
              {t('common.back')}
            </button>
          </nav>

          {/* Debug panel — dev-only (isDebugMode()). Houses the old "Offerte details" fields
              (quote ID / offer date / valid until) plus anything else useful for local testing.
              Not rendered in production builds. Pattern mirrors the product screens
              (InboedelScreen etc.) so the toggle behaviour feels consistent. */}
          {isDebugMode() && (
            <div style={styles.debugPanel}>
              <div style={styles.debugPanelHeader}>
                <h3 style={styles.debugPanelTitle}>Offer debug</h3>
                <button
                  onClick={() => setShowDebugPanel(!showDebugPanel)}
                  style={styles.debugToggleButton}
                >
                  {showDebugPanel ? '▼ Hide' : '▶ Show'}
                </button>
              </div>
              {showDebugPanel && (
                <div style={styles.detailsList}>
                  {quoteId && (
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Offerte ID</span>
                      <span style={styles.detailValue}>{quoteId}</span>
                    </div>
                  )}
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>{t('screens.offerSummary.offerDate')}</span>
                    <span style={styles.detailValue}>{formatDate(today)}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>{t('screens.offerSummary.validUntil')}</span>
                    <span style={styles.detailValue}>{formatDate(validUntil)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default WorkflowSummaryScreen;
